import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Calendar as CalendarIcon, Heart } from "lucide-react";
import VendorCard from "@/components/VendorCard";
import { formatEventDate } from "@/lib/dates";

const statusBadge = {
  requested: "bg-[#F5F2EB] text-[#5C5C5C]",
  quoted: "bg-[#EFEADF] text-[#1A1A1A]",
  accepted: "bg-[#8A9A86] text-white",
  paid: "bg-[#1A1A1A] text-white",
  declined: "bg-[#C05A46]/10 text-[#C05A46]",
  cancelled: "bg-[#F5F2EB] text-[#5C5C5C]",
};

export default function PlannerDashboard() {
  const { user } = useAuth();
  const favContext = useFavorites();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [tab, setTab] = useState("events");
  const [bookingFilter, setBookingFilter] = useState("all");
  const isConfirmed = (b) => ["accepted", "paid"].includes(b.status);
  const visibleBookings = bookingFilter === "confirmed" ? bookings.filter(isConfirmed) : bookings;
  const [open, setOpen] = useState(false);
  const EMPTY_EVENT = { title: "", date: "", location: "", needs_venue: false, guest_count: 50, budget: 5000, notes: "" };
  const [f, setF] = useState(EMPTY_EVENT);
  const closeDialog = (v) => { setOpen(v); if (!v) setF(EMPTY_EVENT); };

  const load = () => {
    api.get("/events").then(({ data }) => setEvents(data));
    api.get("/bookings").then(({ data }) => setBookings(data));
    api.get("/favorites").then(({ data }) => setFavorites(data)).catch(() => setFavorites([]));
  };
  useEffect(() => { load(); }, []);
  // Re-fetch the favorites list whenever a card toggle changes the shared set.
  useEffect(() => {
    api.get("/favorites").then(({ data }) => setFavorites(data)).catch(() => {});
  }, [favContext?.ids]);

  const create = async () => {
    try {
      const payload = f.needs_venue ? { ...f, location: "" } : f;
      const { data } = await api.post("/events", payload);
      toast.success("Event created");
      setOpen(false); setF(EMPTY_EVENT);
      nav(data.needs_venue ? `/browse?event=${data.event_id}&category=venue` : `/events/${data.event_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="planner-dashboard" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8 sm:mb-10 gap-4">
        <div>
          <div className="eyebrow mb-3">Planner Studio</div>
          <h1 className="font-serif text-4xl sm:text-5xl">Hello, {user?.name?.split(" ")[0]}</h1>
        </div>
        <Button data-testid="new-event-btn" onClick={() => setOpen(true)} className="btn-sage rounded-full h-11 px-6 self-start sm:self-auto">
          <Plus className="w-4 h-4 mr-2" /> New event
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10 sm:mb-12">
        <Stat label="Events" v={events.length} testid="stat-events" onClick={() => setTab("events")} />
        <Stat label="Bookings" v={bookings.length} testid="stat-bookings" onClick={() => { setBookingFilter("all"); setTab("bookings"); }} />
        <Stat label="Confirmed" v={bookings.filter(isConfirmed).length} testid="stat-confirmed" onClick={() => { setBookingFilter("confirmed"); setTab("bookings"); }} />
        <Stat label="Favorites" v={favorites.length} testid="stat-favorites" onClick={() => setTab("favorites")} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F5F2EB]">
          <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites">
            <Heart className="w-3.5 h-3.5 mr-1.5" /> Favorites
          </TabsTrigger>
        </TabsList>
        <TabsContent value="events" className="mt-6 sm:mt-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {events.length === 0 && <div className="text-[#5C5C5C]" data-testid="no-events">No events yet. Create one to begin.</div>}
            {events.map((e) => (
              <Link key={e.event_id} to={`/events/${e.event_id}`} data-testid={`event-card-${e.event_id}`} className="card-elegant p-6 block">
                <div className="eyebrow">{formatEventDate(e.date)}</div>
                <div className="font-serif text-3xl mt-2">{e.title}</div>
                <div className="text-sm text-[#5C5C5C] mt-1" data-testid={`event-card-location-${e.event_id}`}>
                  {e.needs_venue && !e.location ? "Venue needed" : (e.location || "Location TBD")}
                </div>
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span>{e.guest_count} guests</span>
                  <span className="font-serif text-xl">${e.budget}</span>
                </div>
              </Link>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="bookings" className="mt-6 sm:mt-8">
          <div className="space-y-3">
            {bookingFilter === "confirmed" && (
              <div className="flex items-center gap-3 text-xs sm:text-sm text-[#5C5C5C]" data-testid="bookings-filter-note">
                <span>Showing confirmed bookings only</span>
                <button type="button" data-testid="bookings-show-all" onClick={() => setBookingFilter("all")} className="underline underline-offset-2 hover:text-[#1A1A1A]">Show all</button>
              </div>
            )}
            {bookings.length === 0 && <div className="text-[#5C5C5C]" data-testid="no-bookings">No bookings yet.</div>}
            {bookings.length > 0 && visibleBookings.length === 0 && <div className="text-[#5C5C5C]" data-testid="no-confirmed-bookings">No confirmed bookings yet.</div>}
            {visibleBookings.map(b => (
              <Link key={b.booking_id} to={`/bookings/${b.booking_id}`} data-testid={`booking-row-${b.booking_id}`}
                className="card-elegant p-4 sm:p-5 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="eyebrow">{b.vendor_category}</div>
                  <div className="font-serif text-xl sm:text-2xl mt-1 truncate">{b.vendor_name}</div>
                  <div className="text-xs sm:text-sm text-[#5C5C5C]">{formatEventDate(b.date)} · {b.guest_count} guests</div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`inline-block text-[10px] sm:text-xs uppercase tracking-widest px-2 sm:px-3 py-1 rounded-full ${statusBadge[b.status]}`}>{b.status}</div>
                  <div className="font-serif text-xl sm:text-2xl mt-2">${b.quote_amount}</div>
                </div>
              </Link>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="favorites" className="mt-6 sm:mt-8">
          {favorites.length === 0 ? (
            <div className="text-[#5C5C5C]" data-testid="no-favorites">
              No favorites yet. Tap the <Heart className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5" /> on any vendor card to save it for later.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="favorites-grid">
              {favorites.map(v => <VendorCard key={v.vendor_id} v={v} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={closeDialog}>
        <DialogContent data-testid="new-event-dialog" className="max-w-lg bg-[#FDFBF7]">
          <DialogHeader><DialogTitle className="font-serif text-3xl">Create event</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input data-testid="event-title" placeholder="Event title" value={f.title} onChange={e => setF({...f, title: e.target.value})} className="bg-white h-11" />
            <Input data-testid="event-date" type="date" value={f.date} onChange={e => setF({...f, date: e.target.value})} className="bg-white h-11" />
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="event-needs-venue-label">
              <Checkbox data-testid="event-needs-venue" checked={f.needs_venue} onCheckedChange={(v) => setF({ ...f, needs_venue: v === true, location: v === true ? "" : f.location })} />
              I need a venue
            </label>
            {f.needs_venue ? (
              <div className="text-xs text-[#5C5C5C]" data-testid="event-needs-venue-note">No location yet — you can find a venue for this event and add the address later.</div>
            ) : (
              <Input data-testid="event-location" placeholder="Location" value={f.location} onChange={e => setF({...f, location: e.target.value})} className="bg-white h-11" />
            )}
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="event-guests" type="number" placeholder="Guests" value={f.guest_count} onChange={e => setF({...f, guest_count: +e.target.value})} className="bg-white h-11" />
              <Input data-testid="event-budget" type="number" placeholder="Budget" value={f.budget} onChange={e => setF({...f, budget: +e.target.value})} className="bg-white h-11" />
            </div>
            <Textarea data-testid="event-notes" placeholder="Notes" value={f.notes} onChange={e => setF({...f, notes: e.target.value})} className="bg-white" />
          </div>
          <DialogFooter>
            <Button data-testid="create-event-submit" className="btn-sage rounded-full" onClick={create}>Create event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, v, onClick, testid }) {
  if (!onClick) {
    return (
      <div className="card-elegant p-6">
        <div className="eyebrow">{label}</div>
        <div className="font-serif text-4xl mt-2">{v}</div>
      </div>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className="card-elegant p-6 text-left w-full cursor-pointer hover:-translate-y-0.5 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8A9A86]">
      <div className="eyebrow">{label}</div>
      <div className="font-serif text-4xl mt-2">{v}</div>
    </button>
  );
}
